import type { VorankuendigungData } from '../../types';
import type { PlanStandFuerAblage } from './PlanVorschau';

/**
 * Abrechnung geplotteter SiGe-Pläne und Vorankündigungen — wer abrechnet und
 * welches Blatt.
 *
 * Beim ersten Versuch eines neuen Auftrags merkt sich die Vorschau des
 * Editors Projekt und Planstand zur Auftrags-ID (`merkePlotAuftrag`). Wer den
 * Auftrag später als angekommen erkennt — der sendende Dialog, ein neu
 * geöffneter (auch im Assistenten) oder die Nachprüfung nach dem Schließen —,
 * meldet die ID (`meldePlotAngekommen`). Gemerkte Aufträge wandern in die
 * offenen Abrechnungen; der Editor DIESES Projekts holt sie ab, sobald kein
 * anderer Abrechnungsschritt offen ist — notfalls beim nächsten Öffnen.
 *
 * So entscheidet, wer den Auftrag anlegt, ob abgerechnet wird (der Assistent
 * merkt nichts), und nicht, wer ihn zufällig auflöst. Abgelegt wird der Stand,
 * mit dem der Auftrag begann: eine Wiederholung schickt dieselbe Datei
 * (plotSendeabsichten.ts, `datei`).
 *
 * Modulweit wie die Sendeabsichten, mit derselben Lebensdauer (der Tab).
 *
 * `quelle` trennt die Module: der SiGe-Plan-Editor holt nur SiGe-Pläne ab,
 * die Vorankündigung nur Vorankündigungen — auch im selben Projekt.
 */

export type PlotQuelle = 'sigeplan' | 'vorank';

/**
 * Geplotteter Stand der Vorankündigung: der Inhalt beim Klick auf „An Plotter
 * senden“. Gebucht und abgelegt wird GENAU er, nicht der Editorstand beim
 * Ankommen des Auftrags.
 *
 * Woher das Blatt kam (Plan Vorankündigung unterschreiben §9): `unterschrieben`
 * = die unterschriebene Fassung (`vorank_fassungen`); `snapshot` ist dann ihr
 * Inhalt, und Archiv und Ablage nach dem Plot nehmen genau ihn. Fehlt
 * `quelle`, gilt `stand` — so schreiben ältere Aufrufer und Tests weiter.
 */
export interface VorankPlotStand {
    readonly snapshot: VorankuendigungData;
    readonly quelle?: 'unterschrieben' | 'stand';
    /** `vorank_fassungen.id` bei `quelle: 'unterschrieben'`, sonst null. */
    readonly fassungId?: string | null;
}

interface StandJeQuelle {
    sigeplan: PlanStandFuerAblage;
    vorank: VorankPlotStand;
}

export type OffenePlotAbrechnung<Q extends PlotQuelle = PlotQuelle> = {
    [K in Q]: {
        readonly auftragId: string;
        /** `projects.id` — abrechnen darf nur der Editor dieses Projekts. */
        readonly projektId: string;
        readonly quelle: K;
        readonly stand: StandJeQuelle[K];
    };
}[Q];

/** Obergrenze: verworfene Aufträge meldet niemand — ältere Einträge fallen heraus. */
const MAX_GEMERKT = 20;

const gemerkt = new Map<string, OffenePlotAbrechnung>();
let offen: readonly OffenePlotAbrechnung[] = [];
const zuhoerer = new Set<() => void>();

const schluessel = (auftragId: string): string => (auftragId ?? '').trim().toLowerCase();

const setzeOffen = (neu: readonly OffenePlotAbrechnung[]) => {
    offen = neu;
    for (const f of zuhoerer) f();
};

/** Neuer Auftrag, abzurechnen im Projekt `projektId`. Der erste Eintrag je ID gilt. */
export const merkePlotAuftrag = <Q extends PlotQuelle>(
    auftragId: string, projektId: string, quelle: Q, stand: StandJeQuelle[Q],
): void => {
    const id = schluessel(auftragId);
    if (!id || !projektId || gemerkt.has(id) || offen.some(o => o.auftragId === id)) return;
    gemerkt.set(id, { auftragId: id, projektId, quelle, stand } as OffenePlotAbrechnung);
    while (gemerkt.size > MAX_GEMERKT) {
        const aeltester = gemerkt.keys().next().value;
        if (aeltester === undefined) break;
        gemerkt.delete(aeltester);
    }
};

/** Angekommen: ein gemerkter Auftrag wird zur offenen Abrechnung. Nicht gemerkt → nichts. */
export const meldePlotAngekommen = (auftragId: string): void => {
    const id = schluessel(auftragId);
    const eintrag = gemerkt.get(id);
    if (!eintrag) return;
    gemerkt.delete(id);
    setzeOffen([...offen, eintrag]);
};

/** Für `useSyncExternalStore`: dieselbe Liste, bis sich etwas ändert. */
export const offenePlotAbrechnungen = (): readonly OffenePlotAbrechnung[] => offen;

export const plotAbrechnungenAbonnieren = (f: () => void): (() => void) => {
    zuhoerer.add(f);
    return () => { zuhoerer.delete(f); };
};

/** Liegt für dieses Projekt und Modul eine offene Abrechnung? */
export const hatOffenePlotAbrechnung = (
    liste: readonly OffenePlotAbrechnung[], projektId: string, quelle: PlotQuelle,
): boolean => liste.some(o => o.projektId === projektId && o.quelle === quelle);

/** Die nächste offene Abrechnung des Projekts aus diesem Modul — herausgenommen; keine → null. */
export const nimmPlotAbrechnung = <Q extends PlotQuelle>(projektId: string, quelle: Q): OffenePlotAbrechnung<Q> | null => {
    const eintrag = offen.find(o => o.projektId === projektId && o.quelle === quelle) ?? null;
    if (eintrag) setzeOffen(offen.filter(o => o !== eintrag));
    // `quelle` ist geprüft — TypeScript verengt über einen Typparameter nicht.
    return eintrag as OffenePlotAbrechnung<Q> | null;
};

/**
 * Abgeholt, aber nicht abgerechnet — die Ansicht wurde mit offener
 * Positionsauswahl verlassen, oder das Projekt wechselte: zurück an den
 * Anfang der offenen Abrechnungen. Sonst wäre der gedruckte Plot nie
 * gebucht, archiviert oder abgelegt; das nächste Öffnen holt ihn wieder.
 */
export const legePlotAbrechnungZurueck = (eintrag: OffenePlotAbrechnung): void => {
    if (offen.some(o => o.auftragId === eintrag.auftragId)) return;
    setzeOffen([eintrag, ...offen]);
};

/** Nur für Tests. */
export const plotAbrechnungenLeeren = (): void => {
    gemerkt.clear();
    setzeOffen([]);
};
